// components/ProductPositionManager.jsx - FIXED SORTING
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../services/api';

const ProductPositionManager = ({ open, onClose, language, onSuccess }) => {
  const [allProducts, setAllProducts] = useState([]);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isDragging, setIsDragging] = useState(false);

  // Sort products by position - CRITICAL FIX
  const sortByPosition = (products) => {
    return [...products].sort((a, b) => {
      // Products with position 0 go to the end
      if (a.product_pos === 0 && b.product_pos !== 0) return 1;
      if (a.product_pos !== 0 && b.product_pos === 0) return -1;
      // Sort by position number
      return a.product_pos - b.product_pos;
    });
  };

  // Fetch all products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/products/all`);
      let productsData = response.data.products || [];
      
      // CRITICAL: Sort by product_pos AFTER fetching
      const sorted = sortByPosition(productsData);
      
      setAllProducts(sorted);
      setDisplayProducts(sorted);
      
      // Extract unique categories for filter
      const uniqueCategories = [...new Map(productsData.map(p => [p.category_id, { id: p.category_id, name: p.category_name }])).values()];
      setCategories(uniqueCategories);
      
      // Check for conflicts (duplicate positions)
      const positionMap = new Map();
      const conflictsList = [];
      sorted.forEach(product => {
        if (product.product_pos !== 0) {
          if (positionMap.has(product.product_pos)) {
            conflictsList.push({
              position: product.product_pos,
              products: [positionMap.get(product.product_pos), product]
            });
          }
          positionMap.set(product.product_pos, product);
        }
      });
      setConflicts(conflictsList);
      
    } catch (error) {
      console.error('Error fetching products:', error);
      setSnackbar({
        open: true,
        message: language === 'ar' ? 'فشل في تحميل المنتجات' : 'Failed to load products',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchProducts();
    }
  }, [open]);

  // Apply filters to displayProducts
  const applyFilters = useCallback(() => {
    let filtered = [...allProducts];
    
    if (searchTerm) {
      filtered = filtered.filter(p => 
        (language === 'ar' ? p.name : p.name_en).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(p => p.category_id === parseInt(categoryFilter));
    }
    
    setDisplayProducts(filtered);
  }, [allProducts, searchTerm, categoryFilter, language]);

  // Run filters when dependencies change
  useEffect(() => {
    if (!isDragging) {
      applyFilters();
    }
  }, [searchTerm, categoryFilter, allProducts, applyFilters, isDragging]);

  // Handle drag end
  const handleDragEnd = (result) => {
    setIsDragging(false);
    
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    
    // Create a copy of the current display products
    const reordered = [...displayProducts];
    
    // Remove the dragged item
    const [draggedItem] = reordered.splice(result.source.index, 1);
    
    // Insert at new position
    reordered.splice(result.destination.index, 0, draggedItem);
    
    // Update positions based on new order (start from 1)
    const updatedDisplay = reordered.map((item, idx) => ({
      ...item,
      product_pos: idx + 1
    }));
    
    // Update the master allProducts array by merging changes
    const updatedAll = allProducts.map(product => {
      const found = updatedDisplay.find(u => u.id === product.id);
      return found || product;
    });
    
    // Sort the updated allProducts by position
    const sortedAll = sortByPosition(updatedAll);
    
    // Update both states
    setDisplayProducts(updatedDisplay);
    setAllProducts(sortedAll);
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send products that have non-zero positions or have changed
      const updates = allProducts
        .filter(product => product.product_pos !== 0)
        .map(product => ({
          id: product.id,
          product_pos: product.product_pos
        }));
      
      await api.post('/admin/products/update-all-positions', {
        products: updates
      });
      
      setSnackbar({
        open: true,
        message: language === 'ar' ? 'تم تحديث ترتيب المنتجات بنجاح' : 'Product positions updated successfully',
        severity: 'success'
      });
      
      onSuccess?.();
      
      // Refresh the list after save to ensure correct order
      await fetchProducts();
      
    } catch (error) {
      console.error('Error saving positions:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || (language === 'ar' ? 'فشل في حفظ الترتيب' : 'Failed to save positions'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchProducts();
  };

  const handleFixConflicts = () => {
    const fixedProducts = [...allProducts];
    const usedPositions = new Set();
    
    fixedProducts.forEach((product, index) => {
      let newPos = index + 1;
      while (usedPositions.has(newPos)) {
        newPos++;
      }
      product.product_pos = newPos;
      usedPositions.add(newPos);
    });
    
    const sortedFixed = sortByPosition(fixedProducts);
    setAllProducts(sortedFixed);
    applyFilters();
    setConflicts([]);
    
    setSnackbar({
      open: true,
      message: language === 'ar' ? 'تم إصلاح التعارضات تلقائياً' : 'Conflicts fixed automatically',
      severity: 'info'
    });
  };

  // Helper function to get item style
  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',
    marginBottom: '8px',
    backgroundColor: isDragging ? '#1A1A1A' : '#0D0D0D',
    border: '1px solid #2D2D2D',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    ...draggableStyle,
    cursor: 'grab'
  });

  const getListStyle = (isDraggingOver) => ({
    background: isDraggingOver ? 'rgba(255, 215, 0, 0.05)' : 'transparent',
    padding: '8px',
    borderRadius: '12px',
    minHeight: '400px'
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            borderRadius: '20px',
            border: '1px solid #FFD700',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #2D2D2D',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 700 }}>
              {language === 'ar' ? 'ترتيب جميع المنتجات' : 'All Products Order'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' 
                ? `إجمالي ${allProducts.length} منتج - اسحب وأفلت لإعادة الترتيب`
                : `${allProducts.length} total products - Drag and drop to reorder`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              size="small"
              sx={{ color: '#B3B3B3' }}
            >
              {language === 'ar' ? 'تحديث' : 'Refresh'}
            </Button>
            <IconButton onClick={onClose} sx={{ color: '#B3B3B3' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Filters Bar */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid #2D2D2D',
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <TextField
              size="small"
              placeholder={language === 'ar' ? 'بحث عن منتج...' : 'Search product...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                },
                '& input': { color: '#FFFFFF' }
              }}
            />
            
            <TextField
              select
              size="small"
              label={language === 'ar' ? 'الفئة' : 'Category'}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{
                minWidth: 150,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                },
                '& .MuiInputLabel-root': { color: '#B3B3B3' },
                '& .MuiSelect-select': { color: '#FFFFFF' }
              }}
            >
              <MenuItem value="all">{language === 'ar' ? 'جميع الفئات' : 'All Categories'}</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>
                  {language === 'ar' ? cat.name : cat.name_en || cat.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {conflicts.length > 0 && (
            <Alert 
              severity="warning"
              icon={<WarningIcon />}
              sx={{ 
                m: 2, 
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                color: '#FF9800',
                border: '1px solid #FF9800',
              }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={handleFixConflicts}
                  sx={{ color: '#FF9800' }}
                >
                  {language === 'ar' ? 'إصلاح تلقائي' : 'Auto Fix'}
                </Button>
              }
            >
              {language === 'ar' 
                ? `تم اكتشاف ${conflicts.length} تعارض في أرقام الترتيب`
                : `Found ${conflicts.length} position conflicts`
              }
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#FFD700' }} />
            </Box>
          ) : displayProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'لا توجد منتجات' : 'No products found'}
              </Typography>
            </Box>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={getListStyle(snapshot.isDraggingOver)}
                  >
                    {displayProducts.map((product, index) => (
                      <Draggable
                        key={product.id.toString()}
                        draggableId={product.id.toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style
                            )}
                          >
                            <Paper sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                              <DragIcon sx={{ color: '#FFD700', cursor: 'grab' }} />
                              
                              <Box sx={{
                                width: 36,
                                height: 36,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#1A1A1A',
                                borderRadius: '8px',
                              }}>
                                <Typography sx={{ color: '#FFD700', fontWeight: 700 }}>
                                  {index + 1}
                                </Typography>
                              </Box>

                              <Box sx={{
                                width: 45,
                                height: 45,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                backgroundColor: '#1A1A1A',
                              }}>
                                <img
                                  src={product.imageUrl || '/placeholder.png'}
                                  alt={product.name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              </Box>

                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                                  {language === 'ar' ? product.name : product.name_en}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                                    {product.price} {language === 'ar' ? 'ر.م' : 'R.m'}
                                  </Typography>
                                  <Chip
                                    label={language === 'ar' ? product.category_name : product.category_name_en || product.category_name}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#1A1A1A',
                                      color: '#B3B3B3',
                                      fontSize: '0.65rem',
                                      height: 20,
                                    }}
                                  />
                                </Box>
                              </Box>

                              <Chip
                                label={`POS: ${product.product_pos === 0 ? 'Auto' : product.product_pos}`}
                                size="small"
                                sx={{
                                  backgroundColor: product.product_pos === 0 ? '#FF9800' : '#1A1A1A',
                                  color: product.product_pos === 0 ? '#000000' : '#B3B3B3',
                                  fontSize: '0.7rem',
                                  height: 24,
                                }}
                              />
                            </Paper>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 2, 
          borderTop: '1px solid #2D2D2D',
          gap: 1,
          justifyContent: 'space-between'
        }}>
          <Typography variant="caption" sx={{ color: '#6B6B6B' }}>
            {language === 'ar' 
              ? 'اسحب المنتجات لإعادة ترتيبها. الموضع 0 = ظهور عشوائي'
              : 'Drag products to reorder. Position 0 = random display'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={onClose}
              sx={{ color: '#B3B3B3' }}
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || loading}
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                '&:hover': { backgroundColor: '#FFE55C' }
              }}
            >
              {saving ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (language === 'ar' ? 'حفظ الترتيب' : 'Save Order')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductPositionManager;